import {
  createContext,
  lazy,
  Suspense,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Route, Routes, useLocation } from 'react-router-dom';
import Footer from './components/Footer';
import Header from './components/Header';
import Loader from './components/Loader';
import ScrollToTop from './components/ScrollToTop';
import i18n, { userLanguage } from './i18n/config';
import { Toast, ToastSeverityType } from 'primereact/toast';
import { useDispatch, useSelector } from 'react-redux';
import { useGetproductmasterQuery } from './services/productMasterApi';
import { cableEntryActions } from './redux/features/cableEntryData';
import { IProduct } from './utils/models/Product';
import { RootState } from './redux/state';
import noImage from './assets/icons/noImage.png';
import i18next from 'i18next';
import getEnv, { getGA } from './utils/getAppEnv';
import { Dialog } from 'primereact/dialog';
import { useMobile } from './hooks/useMobile';
import { useTranslation } from 'react-i18next';

const Home = lazy(() => import('./pages/Home'));
const Configurator = lazy(() => import('./pages/Configurator'));
const SavedQuote = lazy(() => import('./pages/SavedQuote'));
const Product = lazy(() => import('./pages/Product'));
const PageNotFound = lazy(() => import('./pages/PageNotFound'));
const Configure = lazy(() => import('./components/Configure'));
const Specification = lazy(
  () => import('./components/ConfiguratorSpecification')
);
const ViewGrommets = lazy(() => import('./pages/ViewGrommets'));
const ViewAccessories = lazy(() => import('./pages/ViewAccessories'));
const Summary = lazy(() => import('./pages/Summary'));

export interface GlobalStateData {
  showToast: (
    severity: ToastSeverityType,
    summary: string,
    detail: string
  ) => void;
}
interface GlobalCtx extends GlobalStateData {}

export const GlobalContext = createContext<GlobalCtx>({
  showToast: (
    severity: ToastSeverityType,
    summary: string,
    detail: string
  ) => {},
});

const App: React.FC<{}> = () => {
  // , error, isLoading, isFetching, isSuccess
  const { data } = useGetproductmasterQuery('productMaster');
  const location = useLocation();
  const { isInProgressConfiguration } = useSelector(
    (state: RootState) => state.grommetsState
  );
  const { t } = useTranslation();
  const [isFirstTime, setFirstTime] = useState(true);
  const mobile = useMobile();
  const { loader } = useSelector((state: RootState) => state.loader);
  const dispatch = useDispatch();
  const {
    setHomeData,
    setAccessories,
    setModularSystemData,
    setPopupData,
    setFrames,
    setMasterData,
  } = cableEntryActions;
  const homeProductGroup = [
    'Cable Entry Plates',
    'Splitting Entry System',
    'Cable Grommets',
    'Multi-cable grommet',
  ];
  useEffect(() => {
    window.onbeforeunload = function () {
      return 'Data will be lost if you leave the page, are you sure?';
    };
  }, []);

  useEffect(() => {
    if (data) {
      let homeData: IProduct[] = [];
      let accessoriesData: IProduct[] = [];
      let modularSystemData: IProduct[] = [];
      let popupData: IProduct[] = [];
      let frames: IProduct[] = [];
      let masterData: IProduct[] = [];
      data['data']?.forEach((pro: IProduct) => {
        const product = { ...pro };
        product['productImage'] = getImage(data, product);
        product['productImage'] = product['productImage']
          ? product['productImage']
          : noImage;
        product['cableInstallationRangesList'] = filterRange(product);
        product['diameterRanges'] = getDiameterRange(product);
        product['materialTemperatureRange'] = product[
          'materialTemperatureRange'
        ]
          ?.replace('...', 'to')
          ?.replace('….', 'to');
        product['numberOfCables'] =
          product['numberOfCables'] === '-' ? '0' : product['numberOfCables'];
        product['numberOfCablesAlt'] = product['numberOfCables'];
        product['productGroup'] = updateProductGroup(product);
        const isProductGroup = findProduct(homeProductGroup, product);
        const isAccessory = findProduct(['TMS - Accessories'], product);
        const isModularSystem = findProduct(
          ['TMS - Cable Grommets', 'TMS - Frames', 'TMS - Accessories'],
          product
        );
        const isFrame = findProduct(['TMS - Frames'], product);
        const isPopupData = findProduct(['TMS - Cable Grommets'], product);

        isProductGroup && homeData.push({ ...product });
        isAccessory &&
          !['101300', '101301'].includes(product.articleNumber) &&
          accessoriesData.push({ ...product });
        isModularSystem && modularSystemData.push({ ...product });
        isPopupData && popupData.push({ ...product });
        isFrame && frames.push({ ...product });
        const value = masterData.find(
          (masterItem) =>
            masterItem['articleNumber'] === product['articleNumber']
        );
        if (!value) {
          masterData.push({ ...product });
        }
      });
      dispatch(setHomeData(homeData));
      dispatch(setAccessories(accessoriesData));
      dispatch(setModularSystemData(modularSystemData));
      dispatch(setPopupData(popupData));
      dispatch(setFrames(frames));
      dispatch(setMasterData(masterData));
    }
  }, [data]);

  const ref = useRef<Toast>(null);

  const showToast = (
    severity: ToastSeverityType,
    summary: string,
    detail: string
  ) => {
    ref.current &&
      ref.current.show({
        severity,
        summary,
        detail,
      });
  };

  const [GlobalState] = useState<GlobalStateData>({
    showToast,
  });
  const [lang, setLang] = useState(userLanguage);
  useEffect(() => {
    i18n.changeLanguage(lang);
  }, [lang]);
  useEffect(() => {
    (window as any)?.dataLayer?.push({
      environment: getEnv(),
      ...getGA(
        i18next.language,
        window.location.hash.split('?')[0].replace('#/', '')
      ),
    });
  }, [location]);

  const ConfigurePage = isInProgressConfiguration ? (
    <Configure />
  ) : (
    <Specification />
  );
  return (
    <div className="App">
      <GlobalContext.Provider key={'glob-context'} value={{ ...GlobalState }}>
        <Header lang={lang} setLang={setLang} />
        <Toast ref={ref}></Toast>
        <main className="container px-3 py-3  md:px-4 md:py-4 lg:px-5 lg:py-5 ">
          <Suspense fallback={<Loader />}>
            {loader ? <Loader /> : ''}
            {data ? (
              <ScrollToTop>
                <Routes>
                  <Route path="/" element={<Home />}></Route>
                  <Route path="/configurator" element={<Configurator />}>
                    <Route index element={ConfigurePage} />
                    <Route path="start" element={<Specification />}></Route>
                    <Route path="configure" element={<Configure />}></Route>
                  </Route>
                  <Route
                    path="/configurator/accessories"
                    element={<ViewAccessories />}
                  ></Route>
                  <Route
                    path="/configurator/modular"
                    element={<ViewGrommets />}
                  ></Route>
                  <Route
                    path="/product/:productId"
                    element={<Product />}
                  ></Route>
                  <Route path="/saveQuote" element={<SavedQuote />}></Route>
                  <Route path="/summary" element={<Summary />}></Route>
                  <Route path="*" element={<PageNotFound />}></Route>
                </Routes>
                <Dialog
                  draggable={false}
                  modal
                  blockScroll
                  breakpoints={{
                    '2160px': '50vw',
                    '1440px': '60vw',
                    '960px': '85vw',
                    '640px': '90vw',
                  }}
                  style={{ width: '80vw' }}
                  header={t('BE_AWARE').replace(':', '')}
                  visible={isFirstTime && mobile}
                  onHide={() => {
                    setFirstTime(false);
                  }}
                >
                  <>
                    <div>{t('BEST_SUITED')} </div>
                    <div className="flex flex-column md:flex-row justify-content-end mt-3">
                      <button
                        onClick={() => {
                          setFirstTime(false);
                        }}
                        className="primary-btn"
                      >
                        {t('OKAY')}
                      </button>
                    </div>
                  </>
                </Dialog>
              </ScrollToTop>
            ) : (
              <Loader />
            )}
          </Suspense>
        </main>
        <Footer />
      </GlobalContext.Provider>
    </div>
  );
};

export default App;

const findProduct = (
  productGroups: string[],
  eachProduct: IProduct,
  optionalValidatorFn: Function | null = null
) =>
  productGroups.includes(eachProduct['productGroup']) &&
  (optionalValidatorFn ? optionalValidatorFn(eachProduct) : true);

const filterRange = (product: IProduct) =>
  product['cableInstallationRangesList'].filter((range) => range !== '');

const updateProductGroup = (product: IProduct) =>
  product['productGroup'] === 'Multi-cable grommet'
    ? 'Cable Grommets'
    : product['productGroup'];

const getDiameterRange = (product: IProduct) => {
  return Array.isArray(product['cableInstallationRangesList'])
    ? product.cableInstallationRangesList
        .filter((range) => range)
        .map((range) => {
          const splittedInfo = range.split('x')[1]
            ? range.split('x')[1]
            : range;

          const [min, max] = splittedInfo.trim().split('mm')[0].split('-');

          return [Number(min), Number(max) ? Number(max) : Number(min)];
        })
    : [product.cableInstallationRangesList];
};
function getImage(data: any, product: IProduct): string {
  return data['imagesList']?.find(
    (image: { plateImageLocation: string; imageName: string }) =>
      image.imageName.includes(product.articleNumber)
  )?.plateImageLocation;
}
